import { useState, type FormEvent } from 'react'
import { PageHeader, Alert, FormField, card, colors, btnPrimary } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'
import { useSupportEmail } from '../../hooks/use-support-email'

// Self-service account page for any authenticated user: edit own name and
// change own password. Email (login identity) is read-only.
export function MyProfilePage() {
  const { user } = useAuth()
  const supportEmail = useSupportEmail()

  const [name, setName] = useState({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '' })
  const [nameSaving, setNameSaving] = useState(false)
  const [nameSaved, setNameSaved] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' })
  const [pwSaving, setPwSaving] = useState(false)
  const [pwSaved, setPwSaved] = useState(false)
  const [pwError, setPwError] = useState<string | null>(null)

  async function handleNameSave(e: FormEvent): Promise<void> {
    e.preventDefault()
    setNameSaving(true)
    setNameSaved(false)
    setNameError(null)
    try {
      await api.patch('/users/me', { firstName: name.firstName.trim(), lastName: name.lastName.trim() })
      setNameSaved(true)
    } catch (err) {
      setNameError(err instanceof ApiError ? err.message : 'Failed to save name')
    } finally {
      setNameSaving(false)
    }
  }

  async function handlePasswordChange(e: FormEvent): Promise<void> {
    e.preventDefault()
    setPwError(null)
    setPwSaved(false)
    if (pw.newPassword !== pw.confirm) {
      setPwError('New password and confirmation do not match')
      return
    }
    setPwSaving(true)
    try {
      await api.post('/users/me/change-password', {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      })
      setPwSaved(true)
      setPw({ currentPassword: '', newPassword: '', confirm: '' })
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : 'Failed to change password')
    } finally {
      setPwSaving(false)
    }
  }

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Update your name and password" />

      <form onSubmit={handleNameSave} style={{ ...card, marginBottom: '1rem', maxWidth: '480px' }}>
        <h3 style={{ marginTop: 0 }}>Name</h3>
        {nameError && <Alert type="error" message={nameError} onDismiss={() => setNameError(null)} />}
        {nameSaved && <Alert type="success" message="Name updated." onDismiss={() => setNameSaved(false)} />}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <FormField
            name="firstName"
            label="First name"
            value={name.firstName}
            onChange={(v) => setName((p) => ({ ...p, firstName: v }))}
          />
          <FormField
            name="lastName"
            label="Last name"
            value={name.lastName}
            onChange={(v) => setName((p) => ({ ...p, lastName: v }))}
          />
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <FormField name="email" label="Email" value={user?.email ?? ''} disabled />
          <p style={{ marginTop: '0.25rem', marginBottom: 0, fontSize: '13px', color: colors.textMuted }}>
            To change your login email, contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="submit" disabled={nameSaving} style={btnPrimary}>
            {nameSaving ? 'Saving...' : 'Save name'}
          </button>
        </div>
      </form>

      <form onSubmit={handlePasswordChange} style={{ ...card, maxWidth: '480px' }}>
        <h3 style={{ marginTop: 0 }}>Change password</h3>
        {pwError && <Alert type="error" message={pwError} onDismiss={() => setPwError(null)} />}
        {pwSaved && <Alert type="success" message="Password changed." onDismiss={() => setPwSaved(false)} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <FormField
            name="currentPassword"
            label="Current password"
            type="password"
            value={pw.currentPassword}
            onChange={(v) => setPw((p) => ({ ...p, currentPassword: v }))}
            required
          />
          <FormField
            name="newPassword"
            label="New password"
            type="password"
            value={pw.newPassword}
            onChange={(v) => setPw((p) => ({ ...p, newPassword: v }))}
            required
          />
          <FormField
            name="confirmPassword"
            label="Confirm new password"
            type="password"
            value={pw.confirm}
            onChange={(v) => setPw((p) => ({ ...p, confirm: v }))}
            required
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            type="submit"
            disabled={pwSaving || !pw.currentPassword || !pw.newPassword || !pw.confirm}
            style={btnPrimary}
          >
            {pwSaving ? 'Changing...' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  )
}
